/**
 * =============================================================================
 * SERVICARE - FILE TRADUZIONI MULTILINGUA
 * =============================================================================
 * 
 * COME MODIFICARE:
 * 1. Trova la sezione che vuoi modificare (landing, privacy, terms, cookies, common)
 * 2. Modifica il testo nella lingua desiderata (it, en, de, fr)
 * 3. Salva il file - le modifiche saranno visibili immediatamente
 * 
 * STRUTTURA:
 * - landing: Testi della pagina di benvenuto/marketing
 * - privacy: Informativa sulla privacy
 * - terms: Termini e condizioni
 * - cookies: Banner e policy cookie
 * - common: Testi comuni (pulsanti, footer, ecc.)
 * 
 * =============================================================================
 */

export const translations = {
  
  // ===========================================================================
  // LANDING PAGE - Pagina di benvenuto/marketing
  // ===========================================================================
  landing: {
    // Hero Section
    hero: {
      title: {
        it: "Gestione Pasti Intelligente per Strutture Assistenziali",
        en: "Smart Meal Management for Healthcare Facilities",
        de: "Intelligentes Mahlzeitenmanagement für Pflegeeinrichtungen",
        fr: "Gestion Intelligente des Repas pour Établissements de Soins"
      },
      subtitle: {
        it: "Semplifica la gestione di menu, ordini e diete speciali. Una soluzione completa per case di riposo, ospedali e strutture sanitarie.",
        en: "Simplify menu management, orders, and special diets. A complete solution for nursing homes, hospitals, and healthcare facilities.",
        de: "Vereinfachen Sie die Verwaltung von Menüs, Bestellungen und Spezialdiäten. Eine komplette Lösung für Pflegeheime, Krankenhäuser und Gesundheitseinrichtungen.",
        fr: "Simplifiez la gestion des menus, commandes et régimes spéciaux. Une solution complète pour maisons de retraite, hôpitaux et établissements de santé."
      },
      cta: {
        it: "Accedi all'App",
        en: "Access the App",
        de: "Zur App",
        fr: "Accéder à l'App"
      },
      ctaSecondary: {
        it: "Scopri di più",
        en: "Learn More",
        de: "Mehr erfahren",
        fr: "En savoir plus"
      }
    },

    // Features Section
    features: {
      title: {
        it: "Funzionalità Principali",
        en: "Key Features",
        de: "Hauptfunktionen",
        fr: "Fonctionnalités Principales"
      },
      subtitle: {
        it: "Tutto ciò di cui hai bisogno per gestire i pasti nella tua struttura",
        en: "Everything you need to manage meals in your facility",
        de: "Alles, was Sie für die Mahlzeitenverwaltung in Ihrer Einrichtung benötigen",
        fr: "Tout ce dont vous avez besoin pour gérer les repas dans votre établissement"
      },
      items: [
        {
          title: {
            it: "Gestione Menu",
            en: "Menu Management",
            de: "Menüverwaltung",
            fr: "Gestion des Menus"
          },
          description: {
            it: "Crea e gestisci menu settimanali con facilità. Configura piatti, allergeni e varianti.",
            en: "Create and manage weekly menus with ease. Configure dishes, allergens, and variants.",
            de: "Erstellen und verwalten Sie wöchentliche Menüs mit Leichtigkeit. Konfigurieren Sie Gerichte, Allergene und Varianten.",
            fr: "Créez et gérez les menus hebdomadaires facilement. Configurez plats, allergènes et variantes."
          }
        },
        {
          title: {
            it: "Ordini e Comande",
            en: "Orders Management",
            de: "Bestellverwaltung",
            fr: "Gestion des Commandes"
          },
          description: {
            it: "Raccogli le ordinazioni dei pazienti per ogni pasto. Gestisci preferenze e diete speciali.",
            en: "Collect patient orders for each meal. Manage preferences and special diets.",
            de: "Sammeln Sie Patientenbestellungen für jede Mahlzeit. Verwalten Sie Präferenzen und Spezialdiäten.",
            fr: "Collectez les commandes des patients pour chaque repas. Gérez préférences et régimes spéciaux."
          }
        },
        {
          title: {
            it: "Diete e Allergeni",
            en: "Diets & Allergens",
            de: "Diäten & Allergene",
            fr: "Régimes & Allergènes"
          },
          description: {
            it: "Traccia diete specifiche e allergie alimentari. Sicurezza e conformità garantite.",
            en: "Track specific diets and food allergies. Safety and compliance guaranteed.",
            de: "Verfolgen Sie spezifische Diäten und Nahrungsmittelallergien. Sicherheit und Konformität garantiert.",
            fr: "Suivez les régimes spécifiques et allergies alimentaires. Sécurité et conformité garanties."
          }
        },
        {
          title: {
            it: "Stampe e Report",
            en: "Prints & Reports",
            de: "Ausdrucke & Berichte",
            fr: "Impressions & Rapports"
          },
          description: {
            it: "Genera cartellini per i vassoi, riepiloghi cucina e report dettagliati.",
            en: "Generate tray labels, kitchen summaries, and detailed reports.",
            de: "Erstellen Sie Tablettenschilder, Küchenübersichten und detaillierte Berichte.",
            fr: "Générez étiquettes plateaux, résumés cuisine et rapports détaillés."
          }
        },
        {
          title: {
            it: "Multi-Struttura",
            en: "Multi-Facility",
            de: "Multi-Einrichtung",
            fr: "Multi-Établissement"
          },
          description: {
            it: "Gestisci più strutture da un'unica dashboard. Ideale per gruppi e catene.",
            en: "Manage multiple facilities from a single dashboard. Ideal for groups and chains.",
            de: "Verwalten Sie mehrere Einrichtungen von einem Dashboard aus. Ideal für Gruppen und Ketten.",
            fr: "Gérez plusieurs établissements depuis un tableau de bord unique. Idéal pour groupes et chaînes."
          }
        },
        {
          title: {
            it: "App Installabile",
            en: "Installable App",
            de: "Installierbare App",
            fr: "Application Installable"
          },
          description: {
            it: "Installa l'app su qualsiasi dispositivo. Funziona anche offline.",
            en: "Install the app on any device. Works offline too.",
            de: "Installieren Sie die App auf jedem Gerät. Funktioniert auch offline.",
            fr: "Installez l'app sur n'importe quel appareil. Fonctionne aussi hors ligne."
          }
        }
      ]
    },

    // Stats Section
    stats: {
      facilities: {
        it: "Strutture Gestite",
        en: "Managed Facilities",
        de: "Verwaltete Einrichtungen",
        fr: "Établissements Gérés"
      },
      meals: {
        it: "Pasti Serviti",
        en: "Meals Served",
        de: "Servierte Mahlzeiten",
        fr: "Repas Servis"
      },
      satisfaction: {
        it: "Soddisfazione",
        en: "Satisfaction",
        de: "Zufriedenheit",
        fr: "Satisfaction"
      }
    },

    // CTA Section
    cta: {
      title: {
        it: "Pronto a Semplificare la Gestione dei Pasti?",
        en: "Ready to Simplify Meal Management?",
        de: "Bereit, die Mahlzeitenverwaltung zu vereinfachen?",
        fr: "Prêt à Simplifier la Gestion des Repas?"
      },
      subtitle: {
        it: "Contattaci per una demo gratuita o accedi direttamente alla piattaforma.",
        en: "Contact us for a free demo or access the platform directly.",
        de: "Kontaktieren Sie uns für eine kostenlose Demo oder greifen Sie direkt auf die Plattform zu.",
        fr: "Contactez-nous pour une démo gratuite ou accédez directement à la plateforme."
      },
      button: {
        it: "Accedi Ora",
        en: "Access Now",
        de: "Jetzt Zugreifen",
        fr: "Accéder Maintenant"
      },
      contact: {
        it: "Richiedi Demo",
        en: "Request Demo",
        de: "Demo Anfordern",
        fr: "Demander Démo"
      }
    }
  },

  // ===========================================================================
  // PRIVACY POLICY - Informativa sulla Privacy
  // ===========================================================================
  privacy: {
    title: {
      it: "Informativa sulla Privacy",
      en: "Privacy Policy",
      de: "Datenschutzerklärung",
      fr: "Politique de Confidentialité"
    },
    lastUpdate: {
      it: "Ultimo aggiornamento",
      en: "Last updated",
      de: "Letzte Aktualisierung",
      fr: "Dernière mise à jour"
    },
    sections: [
      {
        title: {
          it: "1. Titolare del Trattamento",
          en: "1. Data Controller",
          de: "1. Verantwortlicher",
          fr: "1. Responsable du Traitement"
        },
        content: {
          it: `Il Titolare del trattamento dei dati personali è:

**ServiCare**
[Inserire indirizzo completo]
[Inserire email di contatto]
[Inserire numero di telefono]

Per qualsiasi domanda relativa alla privacy, contattare: privacy@servicare.ch`,
          en: `The Data Controller is:

**ServiCare**
[Insert full address]
[Insert contact email]
[Insert phone number]

For any privacy-related questions, contact: privacy@servicare.ch`,
          de: `Der Verantwortliche für die Datenverarbeitung ist:

**ServiCare**
[Vollständige Adresse einfügen]
[Kontakt-E-Mail einfügen]
[Telefonnummer einfügen]

Bei Fragen zum Datenschutz kontaktieren Sie: privacy@servicare.ch`,
          fr: `Le Responsable du traitement des données est:

**ServiCare**
[Insérer adresse complète]
[Insérer email de contact]
[Insérer numéro de téléphone]

Pour toute question relative à la confidentialité, contactez: privacy@servicare.ch`
        }
      },
      {
        title: {
          it: "2. Dati Raccolti",
          en: "2. Data Collected",
          de: "2. Erhobene Daten",
          fr: "2. Données Collectées"
        },
        content: {
          it: `Raccogliamo i seguenti tipi di dati:

**Dati di accesso:**
- Username e password (criptata)
- Indirizzo IP
- Data e ora di accesso
- Tipo di dispositivo e browser

**Dati operativi:**
- Informazioni sui pazienti (nome, camera, diete, allergie)
- Ordini dei pasti
- Configurazioni menu

**Dati tecnici:**
- Cookie tecnici per il funzionamento del sito
- Log di sistema per sicurezza`,
          en: `We collect the following types of data:

**Access data:**
- Username and password (encrypted)
- IP address
- Date and time of access
- Device type and browser

**Operational data:**
- Patient information (name, room, diets, allergies)
- Meal orders
- Menu configurations

**Technical data:**
- Technical cookies for site operation
- System logs for security`,
          de: `Wir erheben folgende Datenarten:

**Zugangsdaten:**
- Benutzername und Passwort (verschlüsselt)
- IP-Adresse
- Datum und Uhrzeit des Zugriffs
- Gerätetyp und Browser

**Betriebsdaten:**
- Patienteninformationen (Name, Zimmer, Diäten, Allergien)
- Mahlzeitenbestellungen
- Menükonfigurationen

**Technische Daten:**
- Technische Cookies für den Websitebetrieb
- Systemprotokolle für Sicherheit`,
          fr: `Nous collectons les types de données suivants:

**Données d'accès:**
- Nom d'utilisateur et mot de passe (crypté)
- Adresse IP
- Date et heure d'accès
- Type d'appareil et navigateur

**Données opérationnelles:**
- Informations patients (nom, chambre, régimes, allergies)
- Commandes de repas
- Configurations menus

**Données techniques:**
- Cookies techniques pour le fonctionnement du site
- Journaux système pour la sécurité`
        }
      },
      {
        title: {
          it: "3. Finalità del Trattamento",
          en: "3. Purpose of Processing",
          de: "3. Zweck der Verarbeitung",
          fr: "3. Finalité du Traitement"
        },
        content: {
          it: `I dati vengono trattati per le seguenti finalità:

- **Erogazione del servizio**: gestione pasti, menu e ordini
- **Sicurezza**: protezione degli accessi e prevenzione abusi
- **Miglioramento del servizio**: analisi anonime per ottimizzare l'esperienza
- **Obblighi legali**: adempimento di obblighi di legge`,
          en: `Data is processed for the following purposes:

- **Service provision**: meal management, menus, and orders
- **Security**: access protection and abuse prevention
- **Service improvement**: anonymous analysis to optimize experience
- **Legal obligations**: compliance with legal requirements`,
          de: `Daten werden für folgende Zwecke verarbeitet:

- **Dienstleistung**: Mahlzeitenverwaltung, Menüs und Bestellungen
- **Sicherheit**: Zugriffsschutz und Missbrauchsprävention
- **Serviceverbesserung**: Anonyme Analysen zur Optimierung
- **Gesetzliche Pflichten**: Erfüllung rechtlicher Anforderungen`,
          fr: `Les données sont traitées aux fins suivantes:

- **Fourniture du service**: gestion repas, menus et commandes
- **Sécurité**: protection des accès et prévention des abus
- **Amélioration du service**: analyses anonymes pour optimiser l'expérience
- **Obligations légales**: respect des exigences légales`
        }
      },
      {
        title: {
          it: "4. Base Giuridica",
          en: "4. Legal Basis",
          de: "4. Rechtsgrundlage",
          fr: "4. Base Juridique"
        },
        content: {
          it: `Il trattamento dei dati si basa su:

- **Esecuzione contrattuale**: per fornire i servizi richiesti
- **Consenso**: per cookie non essenziali e comunicazioni marketing
- **Interesse legittimo**: per sicurezza e miglioramento del servizio
- **Obbligo legale**: per adempimenti normativi`,
          en: `Data processing is based on:

- **Contract execution**: to provide requested services
- **Consent**: for non-essential cookies and marketing communications
- **Legitimate interest**: for security and service improvement
- **Legal obligation**: for regulatory compliance`,
          de: `Die Datenverarbeitung basiert auf:

- **Vertragserfüllung**: zur Erbringung der angeforderten Dienste
- **Einwilligung**: für nicht-essentielle Cookies und Marketing
- **Berechtigtes Interesse**: für Sicherheit und Serviceverbesserung
- **Rechtliche Verpflichtung**: für regulatorische Compliance`,
          fr: `Le traitement des données est basé sur:

- **Exécution contractuelle**: pour fournir les services demandés
- **Consentement**: pour cookies non-essentiels et communications marketing
- **Intérêt légitime**: pour sécurité et amélioration du service
- **Obligation légale**: pour conformité réglementaire`
        }
      },
      {
        title: {
          it: "5. Conservazione dei Dati",
          en: "5. Data Retention",
          de: "5. Datenspeicherung",
          fr: "5. Conservation des Données"
        },
        content: {
          it: `I dati vengono conservati per il tempo necessario:

- **Dati di accesso**: 12 mesi
- **Dati operativi**: per la durata del rapporto contrattuale + 10 anni
- **Log di sicurezza**: 6 mesi
- **Cookie**: vedi Cookie Policy`,
          en: `Data is retained for the necessary period:

- **Access data**: 12 months
- **Operational data**: duration of contract + 10 years
- **Security logs**: 6 months
- **Cookies**: see Cookie Policy`,
          de: `Daten werden für den erforderlichen Zeitraum aufbewahrt:

- **Zugangsdaten**: 12 Monate
- **Betriebsdaten**: Vertragsdauer + 10 Jahre
- **Sicherheitsprotokolle**: 6 Monate
- **Cookies**: siehe Cookie-Richtlinie`,
          fr: `Les données sont conservées pour la durée nécessaire:

- **Données d'accès**: 12 mois
- **Données opérationnelles**: durée du contrat + 10 ans
- **Journaux de sécurité**: 6 mois
- **Cookies**: voir Politique des Cookies`
        }
      },
      {
        title: {
          it: "6. Diritti dell'Interessato",
          en: "6. Data Subject Rights",
          de: "6. Rechte der Betroffenen",
          fr: "6. Droits des Personnes"
        },
        content: {
          it: `Hai il diritto di:

- **Accesso**: ottenere conferma e copia dei tuoi dati
- **Rettifica**: correggere dati inesatti
- **Cancellazione**: richiedere l'eliminazione dei dati
- **Limitazione**: limitare il trattamento
- **Portabilità**: ricevere i dati in formato strutturato
- **Opposizione**: opporti al trattamento
- **Revoca consenso**: ritirare il consenso in qualsiasi momento

Per esercitare i tuoi diritti, contatta: privacy@servicare.ch`,
          en: `You have the right to:

- **Access**: obtain confirmation and copy of your data
- **Rectification**: correct inaccurate data
- **Erasure**: request data deletion
- **Restriction**: limit processing
- **Portability**: receive data in structured format
- **Object**: object to processing
- **Withdraw consent**: withdraw consent at any time

To exercise your rights, contact: privacy@servicare.ch`,
          de: `Sie haben das Recht auf:

- **Auskunft**: Bestätigung und Kopie Ihrer Daten erhalten
- **Berichtigung**: Unrichtige Daten korrigieren
- **Löschung**: Datenlöschung verlangen
- **Einschränkung**: Verarbeitung einschränken
- **Datenübertragbarkeit**: Daten in strukturiertem Format erhalten
- **Widerspruch**: Der Verarbeitung widersprechen
- **Widerruf**: Einwilligung jederzeit widerrufen

Zur Ausübung Ihrer Rechte kontaktieren Sie: privacy@servicare.ch`,
          fr: `Vous avez le droit de:

- **Accès**: obtenir confirmation et copie de vos données
- **Rectification**: corriger les données inexactes
- **Effacement**: demander la suppression des données
- **Limitation**: limiter le traitement
- **Portabilité**: recevoir les données en format structuré
- **Opposition**: vous opposer au traitement
- **Retrait du consentement**: retirer votre consentement à tout moment

Pour exercer vos droits, contactez: privacy@servicare.ch`
        }
      },
      {
        title: {
          it: "7. Sicurezza",
          en: "7. Security",
          de: "7. Sicherheit",
          fr: "7. Sécurité"
        },
        content: {
          it: `Adottiamo misure di sicurezza tecniche e organizzative:

- Crittografia dei dati in transito (HTTPS/TLS)
- Password criptate con algoritmi sicuri
- Accesso limitato ai dati su base need-to-know
- Backup regolari
- Monitoraggio accessi`,
          en: `We implement technical and organizational security measures:

- Data encryption in transit (HTTPS/TLS)
- Passwords encrypted with secure algorithms
- Data access limited on need-to-know basis
- Regular backups
- Access monitoring`,
          de: `Wir implementieren technische und organisatorische Sicherheitsmaßnahmen:

- Datenverschlüsselung bei Übertragung (HTTPS/TLS)
- Passwörter mit sicheren Algorithmen verschlüsselt
- Datenzugriff auf Need-to-know-Basis beschränkt
- Regelmäßige Backups
- Zugriffsüberwachung`,
          fr: `Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles:

- Chiffrement des données en transit (HTTPS/TLS)
- Mots de passe cryptés avec algorithmes sécurisés
- Accès aux données limité sur base du besoin d'en connaître
- Sauvegardes régulières
- Surveillance des accès`
        }
      },
      {
        title: {
          it: "8. Contatti",
          en: "8. Contact",
          de: "8. Kontakt",
          fr: "8. Contact"
        },
        content: {
          it: `Per domande sulla privacy:

**Email**: privacy@servicare.ch
**Indirizzo**: [Inserire indirizzo]

Per reclami, puoi rivolgerti all'Incaricato federale della protezione dei dati e della trasparenza (IFPDT).`,
          en: `For privacy questions:

**Email**: privacy@servicare.ch
**Address**: [Insert address]

For complaints, you can contact the Federal Data Protection and Information Commissioner (FDPIC).`,
          de: `Bei Fragen zum Datenschutz:

**E-Mail**: privacy@servicare.ch
**Adresse**: [Adresse einfügen]

Bei Beschwerden können Sie sich an den Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten (EDÖB) wenden.`,
          fr: `Pour questions sur la confidentialité:

**Email**: privacy@servicare.ch
**Adresse**: [Insérer adresse]

Pour les réclamations, vous pouvez contacter le Préposé fédéral à la protection des données et à la transparence (PFPDT).`
        }
      }
    ]
  },

  // ===========================================================================
  // TERMS & CONDITIONS - Termini e Condizioni
  // ===========================================================================
  terms: {
    title: {
      it: "Termini e Condizioni",
      en: "Terms and Conditions",
      de: "Allgemeine Geschäftsbedingungen",
      fr: "Conditions Générales"
    },
    lastUpdate: {
      it: "Ultimo aggiornamento",
      en: "Last updated",
      de: "Letzte Aktualisierung",
      fr: "Dernière mise à jour"
    },
    sections: [
      {
        title: {
          it: "1. Accettazione dei Termini",
          en: "1. Acceptance of Terms",
          de: "1. Annahme der Bedingungen",
          fr: "1. Acceptation des Conditions"
        },
        content: {
          it: `Accedendo e utilizzando ServiCare, l'utente accetta di essere vincolato dai presenti Termini e Condizioni. Se non si accettano questi termini, si prega di non utilizzare il servizio.

L'utilizzo del servizio implica l'accettazione integrale di:
- Questi Termini e Condizioni
- L'Informativa sulla Privacy
- La Cookie Policy`,
          en: `By accessing and using ServiCare, the user agrees to be bound by these Terms and Conditions. If you do not accept these terms, please do not use the service.

Use of the service implies full acceptance of:
- These Terms and Conditions
- The Privacy Policy
- The Cookie Policy`,
          de: `Durch den Zugriff auf und die Nutzung von ServiCare erklärt sich der Benutzer mit diesen Allgemeinen Geschäftsbedingungen einverstanden. Wenn Sie diese Bedingungen nicht akzeptieren, nutzen Sie den Dienst bitte nicht.

Die Nutzung des Dienstes impliziert die vollständige Akzeptanz von:
- Diesen Allgemeinen Geschäftsbedingungen
- Der Datenschutzerklärung
- Der Cookie-Richtlinie`,
          fr: `En accédant et en utilisant ServiCare, l'utilisateur accepte d'être lié par les présentes Conditions Générales. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le service.

L'utilisation du service implique l'acceptation intégrale de:
- Ces Conditions Générales
- La Politique de Confidentialité
- La Politique des Cookies`
        }
      },
      {
        title: {
          it: "2. Descrizione del Servizio",
          en: "2. Service Description",
          de: "2. Dienstbeschreibung",
          fr: "2. Description du Service"
        },
        content: {
          it: `ServiCare è una piattaforma web per la gestione dei pasti in strutture assistenziali che permette di:

- Configurare menu settimanali
- Gestire ordini e preferenze dei pazienti
- Tracciare diete speciali e allergie
- Generare report e stampe per la cucina
- Gestire più strutture da un'unica dashboard`,
          en: `ServiCare is a web platform for meal management in healthcare facilities that allows:

- Configure weekly menus
- Manage patient orders and preferences
- Track special diets and allergies
- Generate reports and prints for the kitchen
- Manage multiple facilities from a single dashboard`,
          de: `ServiCare ist eine Webplattform für die Mahlzeitenverwaltung in Pflegeeinrichtungen, die ermöglicht:

- Wöchentliche Menüs konfigurieren
- Patientenbestellungen und -präferenzen verwalten
- Spezialdiäten und Allergien verfolgen
- Berichte und Ausdrucke für die Küche erstellen
- Mehrere Einrichtungen von einem Dashboard verwalten`,
          fr: `ServiCare est une plateforme web pour la gestion des repas dans les établissements de soins qui permet de:

- Configurer les menus hebdomadaires
- Gérer les commandes et préférences des patients
- Suivre les régimes spéciaux et allergies
- Générer rapports et impressions pour la cuisine
- Gérer plusieurs établissements depuis un tableau de bord unique`
        }
      },
      {
        title: {
          it: "3. Account e Accesso",
          en: "3. Account and Access",
          de: "3. Konto und Zugang",
          fr: "3. Compte et Accès"
        },
        content: {
          it: `**Credenziali**: Le credenziali di accesso sono personali e non cedibili. L'utente è responsabile della loro custodia.

**Sicurezza**: L'utente deve:
- Mantenere riservate le proprie credenziali
- Notificare immediatamente accessi non autorizzati
- Non condividere l'account con terzi

**Sospensione**: Ci riserviamo il diritto di sospendere account in caso di violazioni.`,
          en: `**Credentials**: Access credentials are personal and non-transferable. The user is responsible for their safekeeping.

**Security**: The user must:
- Keep credentials confidential
- Immediately notify unauthorized access
- Not share the account with third parties

**Suspension**: We reserve the right to suspend accounts in case of violations.`,
          de: `**Anmeldedaten**: Zugangsdaten sind persönlich und nicht übertragbar. Der Benutzer ist für ihre Aufbewahrung verantwortlich.

**Sicherheit**: Der Benutzer muss:
- Anmeldedaten vertraulich behandeln
- Unbefugten Zugriff sofort melden
- Das Konto nicht mit Dritten teilen

**Sperrung**: Wir behalten uns das Recht vor, Konten bei Verstößen zu sperren.`,
          fr: `**Identifiants**: Les identifiants d'accès sont personnels et non transférables. L'utilisateur est responsable de leur conservation.

**Sécurité**: L'utilisateur doit:
- Garder les identifiants confidentiels
- Signaler immédiatement tout accès non autorisé
- Ne pas partager le compte avec des tiers

**Suspension**: Nous nous réservons le droit de suspendre les comptes en cas de violations.`
        }
      },
      {
        title: {
          it: "4. Uso Consentito",
          en: "4. Permitted Use",
          de: "4. Erlaubte Nutzung",
          fr: "4. Utilisation Autorisée"
        },
        content: {
          it: `L'utente si impegna a:

- Utilizzare il servizio solo per scopi legittimi
- Non tentare di accedere a dati di altri utenti
- Non interferire con il funzionamento del servizio
- Non utilizzare bot o sistemi automatizzati non autorizzati
- Rispettare la privacy dei pazienti e le normative sanitarie`,
          en: `The user agrees to:

- Use the service only for legitimate purposes
- Not attempt to access other users' data
- Not interfere with service operation
- Not use unauthorized bots or automated systems
- Respect patient privacy and healthcare regulations`,
          de: `Der Benutzer verpflichtet sich:

- Den Dienst nur für legitime Zwecke zu nutzen
- Nicht zu versuchen, auf Daten anderer Benutzer zuzugreifen
- Nicht in den Dienstbetrieb einzugreifen
- Keine unbefugten Bots oder automatisierten Systeme zu verwenden
- Die Privatsphäre der Patienten und Gesundheitsvorschriften zu respektieren`,
          fr: `L'utilisateur s'engage à:

- Utiliser le service uniquement à des fins légitimes
- Ne pas tenter d'accéder aux données d'autres utilisateurs
- Ne pas interférer avec le fonctionnement du service
- Ne pas utiliser de bots ou systèmes automatisés non autorisés
- Respecter la vie privée des patients et les réglementations sanitaires`
        }
      },
      {
        title: {
          it: "5. Proprietà Intellettuale",
          en: "5. Intellectual Property",
          de: "5. Geistiges Eigentum",
          fr: "5. Propriété Intellectuelle"
        },
        content: {
          it: `Tutti i diritti di proprietà intellettuale relativi a ServiCare, inclusi software, design, logo e contenuti, sono di proprietà esclusiva di ServiCare o dei suoi licenzianti.

È vietato:
- Copiare, modificare o distribuire il software
- Utilizzare il marchio senza autorizzazione
- Effettuare reverse engineering`,
          en: `All intellectual property rights related to ServiCare, including software, design, logo, and content, are the exclusive property of ServiCare or its licensors.

It is forbidden to:
- Copy, modify, or distribute the software
- Use the trademark without authorization
- Perform reverse engineering`,
          de: `Alle geistigen Eigentumsrechte im Zusammenhang mit ServiCare, einschließlich Software, Design, Logo und Inhalte, sind ausschließliches Eigentum von ServiCare oder seinen Lizenzgebern.

Es ist verboten:
- Die Software zu kopieren, zu modifizieren oder zu verteilen
- Die Marke ohne Genehmigung zu verwenden
- Reverse Engineering durchzuführen`,
          fr: `Tous les droits de propriété intellectuelle relatifs à ServiCare, y compris logiciel, design, logo et contenus, sont la propriété exclusive de ServiCare ou de ses concédants.

Il est interdit de:
- Copier, modifier ou distribuer le logiciel
- Utiliser la marque sans autorisation
- Effectuer de l'ingénierie inverse`
        }
      },
      {
        title: {
          it: "6. Limitazione di Responsabilità",
          en: "6. Limitation of Liability",
          de: "6. Haftungsbeschränkung",
          fr: "6. Limitation de Responsabilité"
        },
        content: {
          it: `ServiCare è fornito "così com'è". Non garantiamo che il servizio sia privo di errori o interruzioni.

Non siamo responsabili per:
- Danni indiretti o consequenziali
- Perdita di dati dovuta a cause di forza maggiore
- Uso improprio del servizio da parte dell'utente
- Decisioni prese sulla base dei dati nel sistema`,
          en: `ServiCare is provided "as is". We do not guarantee that the service is error-free or uninterrupted.

We are not responsible for:
- Indirect or consequential damages
- Data loss due to force majeure
- Improper use of the service by the user
- Decisions made based on data in the system`,
          de: `ServiCare wird "wie besehen" bereitgestellt. Wir garantieren nicht, dass der Dienst fehlerfrei oder unterbrechungsfrei ist.

Wir sind nicht verantwortlich für:
- Indirekte oder Folgeschäden
- Datenverlust aufgrund höherer Gewalt
- Unsachgemäße Nutzung des Dienstes durch den Benutzer
- Entscheidungen auf Basis der Daten im System`,
          fr: `ServiCare est fourni "tel quel". Nous ne garantissons pas que le service est exempt d'erreurs ou d'interruptions.

Nous ne sommes pas responsables de:
- Dommages indirects ou consécutifs
- Perte de données due à force majeure
- Utilisation incorrecte du service par l'utilisateur
- Décisions prises sur la base des données du système`
        }
      },
      {
        title: {
          it: "7. Modifiche ai Termini",
          en: "7. Changes to Terms",
          de: "7. Änderungen der Bedingungen",
          fr: "7. Modifications des Conditions"
        },
        content: {
          it: `Ci riserviamo il diritto di modificare questi Termini in qualsiasi momento. Le modifiche saranno comunicate tramite:

- Notifica nell'applicazione
- Email agli utenti registrati

L'uso continuato del servizio dopo le modifiche costituisce accettazione dei nuovi termini.`,
          en: `We reserve the right to modify these Terms at any time. Changes will be communicated via:

- Notification in the application
- Email to registered users

Continued use of the service after changes constitutes acceptance of the new terms.`,
          de: `Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern. Änderungen werden mitgeteilt durch:

- Benachrichtigung in der Anwendung
- E-Mail an registrierte Benutzer

Die fortgesetzte Nutzung des Dienstes nach Änderungen gilt als Annahme der neuen Bedingungen.`,
          fr: `Nous nous réservons le droit de modifier ces Conditions à tout moment. Les modifications seront communiquées via:

- Notification dans l'application
- Email aux utilisateurs enregistrés

L'utilisation continue du service après les modifications constitue l'acceptation des nouvelles conditions.`
        }
      },
      {
        title: {
          it: "8. Legge Applicabile",
          en: "8. Applicable Law",
          de: "8. Anwendbares Recht",
          fr: "8. Droit Applicable"
        },
        content: {
          it: `Questi Termini sono regolati dalla legge svizzera. Per qualsiasi controversia sarà competente il foro di [Inserire città], Svizzera.`,
          en: `These Terms are governed by Swiss law. For any dispute, the court of [Insert city], Switzerland shall have jurisdiction.`,
          de: `Diese Bedingungen unterliegen dem schweizerischen Recht. Für alle Streitigkeiten ist das Gericht in [Stadt einfügen], Schweiz zuständig.`,
          fr: `Ces Conditions sont régies par le droit suisse. Pour tout litige, le tribunal de [Insérer ville], Suisse sera compétent.`
        }
      },
      {
        title: {
          it: "9. Contatti",
          en: "9. Contact",
          de: "9. Kontakt",
          fr: "9. Contact"
        },
        content: {
          it: `Per domande sui Termini e Condizioni:

**Email**: info@servicare.ch
**Indirizzo**: [Inserire indirizzo completo]`,
          en: `For questions about the Terms and Conditions:

**Email**: info@servicare.ch
**Address**: [Insert full address]`,
          de: `Bei Fragen zu den Allgemeinen Geschäftsbedingungen:

**E-Mail**: info@servicare.ch
**Adresse**: [Vollständige Adresse einfügen]`,
          fr: `Pour questions sur les Conditions Générales:

**Email**: info@servicare.ch
**Adresse**: [Insérer adresse complète]`
        }
      }
    ]
  },

  // ===========================================================================
  // COOKIE POLICY - Banner e Policy Cookie
  // ===========================================================================
  cookies: {
    banner: {
      title: {
        it: "Utilizziamo i Cookie",
        en: "We Use Cookies",
        de: "Wir verwenden Cookies",
        fr: "Nous utilisons des Cookies"
      },
      description: {
        it: "Questo sito utilizza cookie tecnici necessari al funzionamento e cookie analitici per migliorare la tua esperienza. Puoi accettare tutti i cookie o personalizzare le tue preferenze.",
        en: "This site uses technical cookies necessary for operation and analytical cookies to improve your experience. You can accept all cookies or customize your preferences.",
        de: "Diese Website verwendet technische Cookies, die für den Betrieb erforderlich sind, und analytische Cookies zur Verbesserung Ihrer Erfahrung. Sie können alle Cookies akzeptieren oder Ihre Einstellungen anpassen.",
        fr: "Ce site utilise des cookies techniques nécessaires au fonctionnement et des cookies analytiques pour améliorer votre expérience. Vous pouvez accepter tous les cookies ou personnaliser vos préférences."
      },
      acceptAll: {
        it: "Accetta Tutti",
        en: "Accept All",
        de: "Alle Akzeptieren",
        fr: "Tout Accepter"
      },
      rejectAll: {
        it: "Rifiuta",
        en: "Reject",
        de: "Ablehnen",
        fr: "Refuser"
      },
      customize: {
        it: "Personalizza",
        en: "Customize",
        de: "Anpassen",
        fr: "Personnaliser"
      },
      save: {
        it: "Salva Preferenze",
        en: "Save Preferences",
        de: "Einstellungen Speichern",
        fr: "Enregistrer Préférences"
      }
    },
    categories: {
      necessary: {
        title: {
          it: "Cookie Necessari",
          en: "Necessary Cookies",
          de: "Notwendige Cookies",
          fr: "Cookies Nécessaires"
        },
        description: {
          it: "Essenziali per il funzionamento del sito. Non possono essere disattivati.",
          en: "Essential for website operation. Cannot be disabled.",
          de: "Unerlässlich für den Websitebetrieb. Können nicht deaktiviert werden.",
          fr: "Essentiels au fonctionnement du site. Ne peuvent pas être désactivés."
        }
      },
      analytics: {
        title: {
          it: "Cookie Analitici",
          en: "Analytics Cookies",
          de: "Analytische Cookies",
          fr: "Cookies Analytiques"
        },
        description: {
          it: "Ci aiutano a capire come utilizzi il sito per migliorare l'esperienza.",
          en: "Help us understand how you use the site to improve the experience.",
          de: "Helfen uns zu verstehen, wie Sie die Website nutzen, um die Erfahrung zu verbessern.",
          fr: "Nous aident à comprendre comment vous utilisez le site pour améliorer l'expérience."
        }
      },
      functional: {
        title: {
          it: "Cookie Funzionali",
          en: "Functional Cookies",
          de: "Funktionale Cookies",
          fr: "Cookies Fonctionnels"
        },
        description: {
          it: "Permettono funzionalità avanzate come la memorizzazione delle preferenze.",
          en: "Enable advanced features like preference storage.",
          de: "Ermöglichen erweiterte Funktionen wie das Speichern von Präferenzen.",
          fr: "Permettent des fonctionnalités avancées comme la mémorisation des préférences."
        }
      }
    },
    learnMore: {
      it: "Maggiori informazioni",
      en: "Learn more",
      de: "Mehr erfahren",
      fr: "En savoir plus"
    }
  },

  // ===========================================================================
  // COMMON - Elementi comuni
  // ===========================================================================
  common: {
    // Navigation
    nav: {
      home: {
        it: "Home",
        en: "Home",
        de: "Startseite",
        fr: "Accueil"
      },
      login: {
        it: "Accedi",
        en: "Login",
        de: "Anmelden",
        fr: "Connexion"
      },
      privacy: {
        it: "Privacy",
        en: "Privacy",
        de: "Datenschutz",
        fr: "Confidentialité"
      },
      terms: {
        it: "Termini",
        en: "Terms",
        de: "AGB",
        fr: "Conditions"
      },
      cookies: {
        it: "Cookie",
        en: "Cookies",
        de: "Cookies",
        fr: "Cookies"
      }
    },

    // Footer
    footer: {
      rights: {
        it: "Tutti i diritti riservati",
        en: "All rights reserved",
        de: "Alle Rechte vorbehalten",
        fr: "Tous droits réservés"
      },
      madeWith: {
        it: "Fatto con",
        en: "Made with",
        de: "Gemacht mit",
        fr: "Fait avec"
      },
      inSwitzerland: {
        it: "in Svizzera",
        en: "in Switzerland",
        de: "in der Schweiz",
        fr: "en Suisse"
      }
    },

    // Language names
    languages: {
      it: "Italiano",
      en: "English",
      de: "Deutsch",
      fr: "Français"
    },

    // Buttons
    buttons: {
      back: {
        it: "Indietro",
        en: "Back",
        de: "Zurück",
        fr: "Retour"
      },
      close: {
        it: "Chiudi",
        en: "Close",
        de: "Schließen",
        fr: "Fermer"
      },
      confirm: {
        it: "Conferma",
        en: "Confirm",
        de: "Bestätigen",
        fr: "Confirmer"
      }
    }
  }
};

// ===========================================================================
// HELPER FUNCTION - Per ottenere il testo tradotto
// ===========================================================================
export const getTranslation = (obj, lang = 'it') => {
  if (!obj) return '';
  return obj[lang] || obj['it'] || '';
};

export default translations;
